import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PackageSearch, AlertTriangle } from "lucide-react";

const MESI = [3, 6, 12];

/**
 * Quando finiscono i DPI a scaffale.
 *
 * Consumo degli ultimi mesi diviso per il periodo, confrontato con la
 * giacenza. E' il conto che si farebbe a mano, fatto su tutti gli articoli e
 * ordinato per urgenza: prima quelli che finiscono prima.
 */
export default function PrevisioneDpi() {
  const [mesi, setMesi] = useState(6);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);

  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      const { data } = await api.get(`/reports/dpi-previsione?mesi=${mesi}`);
      setDati(data);
    } catch { toast.error("Errore caricamento previsione DPI"); }
    finally { setCaricando(false); }
  }, [mesi]);

  useEffect(() => { carica(); }, [carica]);

  const tono = (a) => {
    if (a.mesi_residui == null) return "";
    if (a.mesi_residui <= 1) return "bg-red-50";
    if (a.mesi_residui <= 2) return "bg-amber-50";
    return "";
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-bold">DPI: quando finiscono</h3>
            <p className="text-xs text-slate-500 mt-1">
              Consumo medio a confronto con la giacenza. Prima quelli che si esauriscono prima.
            </p>
          </div>
          <div className="flex gap-1">
            {MESI.map(m => (
              <button key={m} type="button" onClick={() => setMesi(m)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        mesi === m ? "bg-orange-500 text-white border-orange-500"
                                   : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                ultimi {m} mesi
              </button>
            ))}
          </div>
        </div>

        {caricando && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}

        {!caricando && dati && (<>
          {(dati.in_esaurimento > 0 || dati.sotto_scorta > 0) && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                {dati.in_esaurimento > 0 && (
                  <div><b>{dati.in_esaurimento}</b> articoli finiscono entro due mesi.</div>
                )}
                {dati.sotto_scorta > 0 && (
                  <div><b>{dati.sotto_scorta}</b> articoli sono già sotto la scorta minima.</div>
                )}
              </div>
            </div>
          )}

          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left p-2">Articolo</th>
                  <th className="text-right p-2">Giacenza</th>
                  <th className="text-right p-2">Consumo mensile</th>
                  <th className="text-left p-2">Quanto dura</th>
                  <th className="text-left p-2">Taglie più richieste</th>
                </tr>
              </thead>
              <tbody>
                {dati.articoli.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nessun articolo</td></tr>
                )}
                {dati.articoli.map(a => (
                  <tr key={a.item_id} className={`border-t border-slate-100 ${tono(a)}`}>
                    <td className="p-2">
                      <div className="font-medium">{a.articolo}</div>
                      <div className="text-[10px] text-slate-400">{a.categoria}</div>
                    </td>
                    <td className="p-2 text-right">
                      {a.giacenza}
                      {a.sotto_scorta && (
                        <div className="text-[10px] text-red-600">
                          minimo {a.scorta_minima}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {a.al_mese > 0 ? a.al_mese : "—"}
                      <div className="text-[10px] text-slate-400">
                        {a.consumati} in {dati.mesi} mesi
                      </div>
                    </td>
                    <td className="p-2">
                      {a.mesi_residui == null ? (
                        <span className="text-xs text-slate-400">mai consumato nel periodo</span>
                      ) : (
                        <Badge className={`border-0 text-[11px] ${
                          a.mesi_residui <= 1 ? "bg-red-100 text-red-700"
                          : a.mesi_residui <= 2 ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-700"}`}>
                          {a.mesi_residui < 1
                            ? "meno di un mese"
                            : `circa ${a.mesi_residui} mesi`}
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 text-[11px] text-slate-500">
                      {Object.keys(a.taglie).length === 0
                        ? "—"
                        : Object.entries(a.taglie).slice(0, 4)
                            .map(([t, q]) => `${t}: ${q}`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dati.persone.length > 0 && (
            <div>
              <h4 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
                <PackageSearch className="w-4 h-4 text-slate-400" />
                Consumo per persona, a confronto con la media della mansione
              </h4>
              <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {dati.persone.map(p => (
                  <div key={p.employee_id}
                       className={`p-2 flex items-center justify-between gap-3 ${p.sopra_media ? "bg-amber-50" : ""}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.dipendente}</div>
                      <div className="text-[10px] text-slate-500">{p.mansione}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold">{p.pezzi} pezzi</div>
                      <div className="text-[10px] text-slate-500">
                        media {p.media_mansione} · {p.consegne} consegne
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                In giallo chi ha ricevuto più del 50% oltre la media della sua mansione. Non è
                di per sé un problema: può essere un lavoro più usurante o una taglia difficile.
              </p>
            </div>
          )}

          <p className="text-[11px] text-slate-500">
            La durata residua vale se il consumo resta quello dei mesi passati. Un articolo
            introdotto da poco, o usato solo in una stagione, darà un numero poco affidabile.
          </p>
        </>)}
      </CardContent>
    </Card>
  );
}
